/**
 * Huitzi chat service — talks to the backend assistant endpoints.
 *
 * Two flavors:
 *   - chatPublic:        for landing visitors. No auth, knowledge-only.
 *   - chatAuthenticated: for logged-in users. Can trigger actions
 *                        (generate plan, mark favorite plant, etc.).
 *
 * Falls back gracefully if the backend endpoint isn't deployed yet by
 * routing to /api/v1/rag/chat (auth) for logged users, otherwise returning
 * a clear error message that the visitor can understand.
 */
import axios from 'axios';
import { API_BASE_URL as ROOT_API } from '../config/api';

// The rest of the app reads `REACT_APP_API_BASE_URL` from the Docker build
// args and exposes it via config/api.js. This module previously read a
// DIFFERENT variable (`REACT_APP_API_URL`), so in production it fell back
// to the localhost dev URL — the browser tried to POST to
// http://localhost:8000/api/v1/huitzi/chat-public, CSP blocked it as a
// `connect-src` violation, and axios threw a network error so the user
// saw the "jardín de conocimiento" fallback message regardless of auth.
// Reuse the canonical config so the URL stays in sync everywhere.
const API_BASE_URL = `${ROOT_API}/api/v1`;

const HISTORY_KEY = 'huitzi.history.v1';
const MAX_HISTORY = 40;

function getStoredHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function setStoredHistory(history) {
  try {
    const trimmed = history.slice(-MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  } catch (_) {
    // localStorage unavailable in private mode etc. — fail silently.
  }
}

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// The AI answer takes 5-20s; without an explicit timeout a dead connection
// hangs the chat forever, and with a short one we abort answers mid-flight.
const CHAT_TIMEOUT_MS = 45000;

// err.response missing = the request never got an HTTP answer (offline,
// connection dropped, CSP). Tell the user THAT instead of a generic shrug.
function fallbackReply(err) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline || !err?.response) {
    return 'Parece que no hay conexión en este momento 🌿 Revisa tu internet e inténtalo de nuevo.';
  }
  return 'No pude llegar al jardín de conocimiento ahora. Inténtalo en un momento.';
}

const huitziService = {
  getHistory: getStoredHistory,
  clearHistory() { setStoredHistory([]); },

  appendMessage(role, content, meta = {}) {
    const history = getStoredHistory();
    const entry = { role, content, ts: Date.now(), ...meta };
    history.push(entry);
    setStoredHistory(history);
    return entry;
  },

  /**
   * Send a message. Routes to authenticated /huitzi/chat when logged in,
   * to /huitzi/chat-public otherwise. Returns { reply, suggested_actions }.
   *
   * Resilience ladder for the authenticated path (the access cookie
   * ni_access expires after 30 min, so 401s are routine on long sessions):
   *   1. POST /huitzi/chat (cookie auth)
   *   2. on 401  → POST /auth/refresh (uses 7-day ni_refresh cookie) → retry once
   *   3. on 404  → POST /rag/chat (legacy endpoint)
   *   4. last resort → POST /huitzi/chat-public so the user ALWAYS gets a
   *      knowledge answer instead of the dead "jardín de conocimiento" error.
   */
  async sendMessage(message, { isAuthenticated = false } = {}) {
    const recent = getStoredHistory().slice(-6).map(({ role, content }) => ({ role, content }));
    const body = { message, history: recent };
    const authed = { headers: authHeaders(), withCredentials: true, timeout: CHAT_TIMEOUT_MS };

    const postPublic = async () => {
      const r = await axios.post(`${API_BASE_URL}/huitzi/chat-public`, body, {
        withCredentials: true,
        timeout: CHAT_TIMEOUT_MS,
      });
      return r.data;
    };

    // Anonymous visitors: knowledge-only endpoint. One automatic retry —
    // the answer takes ~10-20s and flaky mobile connections drop it often.
    if (!isAuthenticated) {
      try {
        return await postPublic();
      } catch (firstErr) {
        try {
          return await postPublic();
        } catch (err) {
          return {
            reply: fallbackReply(err),
            error: err.response?.data?.detail || err.message,
          };
        }
      }
    }

    // Authenticated users.
    try {
      const r = await axios.post(`${API_BASE_URL}/huitzi/chat`, body, authed);
      return r.data;
    } catch (err) {
      const status = err.response?.status;

      // Session expired → refresh the access cookie and retry once.
      if (status === 401) {
        try {
          await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
          const retry = await axios.post(`${API_BASE_URL}/huitzi/chat`, body, authed);
          return retry.data;
        } catch (_) {
          // refresh/retry failed — fall through to public knowledge mode.
        }
      }

      // Older deploys: personalized endpoint missing → legacy RAG chat.
      if (status === 404) {
        try {
          const fb = await axios.post(
            `${API_BASE_URL}/rag/chat`,
            { message, include_context: true, include_search_results: true },
            authed,
          );
          return {
            reply: fb.data?.response || fb.data?.message || 'Estoy aprendiendo. Vuelve a intentar.',
            suggested_actions: [],
            via_fallback: true,
          };
        } catch (_) { /* fall through */ }
      }

      // Last resort: answer in knowledge-only mode so the chat never dies.
      try {
        const pub = await postPublic();
        return { ...pub, via_fallback: true };
      } catch (finalErr) {
        return {
          reply: fallbackReply(finalErr),
          error: finalErr.response?.data?.detail || finalErr.message,
        };
      }
    }
  },
};

export default huitziService;
